import { Box, Button, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import Dialog from 'components/dialog';
import NewList from 'components/new-list';
import PdfViewer from 'components/pdf-viewer';
import { useAccountState } from 'contexts/account/account-context';
import { AddressAccess } from 'contexts/account/types/account-company.type';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { saveState } from 'utils/storage';
import { useOrderFeedState } from 'views/order-feed/context/order-feed-context';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { getDefaultDispatchDate, getSiteTransportCompanies } from 'views/order-feed/features/my-supply-plan/utils/order-utils';
import { HistoricalOrder } from 'views/order-feed/features/order-history/types/historical-order.type';
import { OrderAttachment } from 'views/order-feed/types/order-attachment.type';

type Props = {
  order: HistoricalOrder;
  site: AddressAccess | undefined;
};

const PAGE_NAME_ID = 'orderFeed.orderHistory.ordersList';

const OrderHistoryRowBody: React.FC<Props> = ({ order, site }) => {
  const { account } = useAccountState();

  const history = useHistory();

  const {
    dispatchDatesQuery: { data: dispatchSchedule },
  } = useOrderFeedState();

  const defaultDispatchDate = getDefaultDispatchDate(dispatchSchedule);

  const handleSaveOrderData = (data: CreateOrderFormValues) => {
    saveState({ key: 'createOrderData', state: data });

        history.push(`/order-feed/supply-plan/${site?.siteErpId}/${site?.id}`);
  };

  const orderDocumentation = useMemo(() => {
    return {
      documentIds: order.documentIds,
      orderNumber: order.orderNumber,
      dispatchDate: order.dispatchDate,
      siteName: order.customerName,
    } as OrderAttachment;
  }, [order]);

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2}>
      <NewList size="medium" aria-label="Active order body list">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography fontWeight={'bold'} fontSize={12}>
                <FormattedMessage id={`${PAGE_NAME_ID}.articleNumber`} />
              </Typography>
            </TableCell>
            <TableCell align="left">
              <Typography fontWeight={'bold'} fontSize={12}>
                <FormattedMessage id={`${PAGE_NAME_ID}.product`} />
              </Typography>
            </TableCell>
            <TableCell align="left">
              <Typography fontWeight={'bold'} fontSize={12}>
                <FormattedMessage id={`${PAGE_NAME_ID}.orderedQuantity`} />
              </Typography>
            </TableCell>
            <TableCell align="left">
              <Typography fontWeight={'bold'} fontSize={12}>
                <FormattedMessage id={`${PAGE_NAME_ID}.invoicedQuantity`} />
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography fontWeight={'bold'} fontSize={12}>
                <FormattedMessage id={`${PAGE_NAME_ID}.deliveredQuantity`} />
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {order.orderLines.map((line) => (
            <TableRow key={`${line.itemNumber}_${line.orderedQuantity}`}>
              <TableCell component="th" scope="row">
                <Typography fontSize={14}>{Number(line.itemNumber)}</Typography>
              </TableCell>
              <TableCell>
                <Typography fontSize={14}>{line.itemName}</Typography>
              </TableCell>
              <TableCell>
                <Typography fontSize={14}>
                  {line.orderedQuantity.toLocaleString(navigator.language, { minimumFractionDigits: 0 })}kg
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontSize={14}>
                  {line.invoicedQuantity.toLocaleString(navigator.language, { minimumFractionDigits: 0 })}kg
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography fontSize={14}>
                  {line.orderedQuantity.toLocaleString(navigator.language, { minimumFractionDigits: 0 })}kg
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </NewList>
      <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={4}>
        {Number(order.documentIds?.length) > 0 && order.documentIds != null && (
          <Dialog
            maxWidth="md"
            title={`Order #${order.orderNumber} delivery documents`}
            button={
              <Button variant="outlined" size="small" type="button">
                <FormattedMessage id={`orderFeed.viewOrder.deliveryNotes`} />
              </Button>
            }>
            <PdfViewer orderAttachment={orderDocumentation} from={'order-history'} />
          </Dialog>
        )}
        <Button
          size="small"
          onClick={() => {
            const deliveryMethods = getSiteTransportCompanies(account.company?.addressAccess, site?.id || '');
            const deliveryMethod = deliveryMethods?.length === 1 ? deliveryMethods[0] : undefined;
            const { key: transportMethod = '', name: addressId = '' } = deliveryMethod ?? {};

            handleSaveOrderData({
              transportMethod,
              addressId,
              customerOrderReference: '',
              orderComment: '',
              requestedDispatchDate: defaultDispatchDate || '',
              siteErpId: site?.siteErpId || '',
              orderLines: order.orderLines.map((line) => ({
                itemNumber: line.itemNumber,
                orderQuantity: line.orderedQuantity,
              })),
            });
          }}>
          <FormattedMessage id={`general.repeatOrder`} />
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(OrderHistoryRowBody);
